import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Grid,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import TextField from '@mui/material/TextField';
import { userService } from '../../services/userService';
import { format } from 'date-fns';

interface UserAttribute {
  key_name: string;
  key_value: string;
  created_date: string;
}

interface UserDetailsData {
  id: number;
  name: string;
  created_date: string;
  attributes: UserAttribute[];
}

const UserDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [userDetails, setUserDetails] = useState<UserDetailsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [biometryImage, setBiometryImage] = useState<string | null>(null);
  const [loadingImage, setLoadingImage] = useState(true);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingAttribute, setDeletingAttribute] = useState<UserAttribute | null>(null);
  const [isDeletingAttribute, setIsDeletingAttribute] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newAttribute, setNewAttribute] = useState({ key_name: '', key_value: '' });
  const [isCreatingAttribute, setIsCreatingAttribute] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingName, setEditingName] = useState('');
  const [isUpdatingName, setIsUpdatingName] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      console.info('==== Loading User Details');

      try {
        setLoading(true);
        setLoadingImage(true);
        setError(null);
        
        const [detailsResponse, imageResponse] = await Promise.all([
          userService.getUserDetails(Number(id)),
          userService.getUserBiometryImage(Number(id))
        ]);

        setUserDetails(detailsResponse);
        setBiometryImage(imageResponse);
      } catch (err) {
        setError('Erro ao carregar dados do usuário. Tente novamente mais tarde.');
        console.error('Error fetching user data:', err);
      } finally {
        setLoading(false);
        setLoadingImage(false);
      }
    };

    fetchData();
  }, [id]);

  const handleImageChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setSelectedImage(file);

      // Criar preview da imagem
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const convertImageToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result as string;
        const base64 = base64String.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleUpdateBiometry = async () => {
    if (!selectedImage || !id) return;

    try {
      setIsUpdating(true);
      setError(null);

      const base64Image = await convertImageToBase64(selectedImage);
      await userService.updateBiometry(Number(id), base64Image);

      // Recarrega os dados do usuário
      const [detailsResponse, imageResponse] = await Promise.all([
        userService.getUserDetails(Number(id)),
        userService.getUserBiometryImage(Number(id))
      ]);

      setUserDetails(detailsResponse);
      setBiometryImage(imageResponse);
      setSelectedImage(null);
      setImagePreview(null);
    } catch (err) {
      setError('Erro ao atualizar biometria. Tente novamente mais tarde.');
      console.error('Error updating biometry:', err);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteBiometry = async () => {
    if (!id) return;

    try {
      setIsDeleting(true);
      setError(null);

      await userService.deleteBiometry(Number(id));

      // Recarrega os dados do usuário
      const [detailsResponse, imageResponse] = await Promise.all([
        userService.getUserDetails(Number(id)),
        userService.getUserBiometryImage(Number(id))
      ]);

      setUserDetails(detailsResponse);
      setBiometryImage(imageResponse);
      setShowDeleteDialog(false);
    } catch (err) {
      setError('Erro ao deletar biometria. Tente novamente mais tarde.');
      console.error('Error deleting biometry:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteAttribute = async () => {
    if (!id || !deletingAttribute) return;

    try {
      setIsDeletingAttribute(true);
      setError(null);

      await userService.deleteAttribute(Number(id), {
        key_name: deletingAttribute.key_name,
        key_value: deletingAttribute.key_value,
      });

      // Recarrega os dados do usuário
      const detailsResponse = await userService.getUserDetails(Number(id));
      setUserDetails(detailsResponse);
      setDeletingAttribute(null);
    } catch (err) {
      setError('Erro ao deletar atributo. Tente novamente mais tarde.');
      console.error('Error deleting attribute:', err);
    } finally {
      setIsDeletingAttribute(false);
    }
  };

  const handleCreateAttribute = async () => {
    if (!id) return;

    try {
      setIsCreatingAttribute(true);
      setError(null);

      await userService.createAttribute(Number(id), newAttribute);

      // Recarrega os dados do usuário
      const detailsResponse = await userService.getUserDetails(Number(id));
      setUserDetails(detailsResponse);
      setShowCreateDialog(false);
      setNewAttribute({ key_name: '', key_value: '' });
    } catch (err) {
      setError('Erro ao criar atributo. Tente novamente mais tarde.');
      console.error('Error creating attribute:', err);
    } finally {
      setIsCreatingAttribute(false);
    }
  };

  const handleUpdateName = async () => {
    if (!id) return;

    try {
      setIsUpdatingName(true);
      setError(null);

      await userService.updateUser(Number(id), { name: editingName });

      // Recarrega os dados do usuário
      const detailsResponse = await userService.getUserDetails(Number(id));
      setUserDetails(detailsResponse);
      setIsEditingName(false);
    } catch (err) {
      setError('Erro ao atualizar nome. Tente novamente mais tarde.');
      console.error('Error updating user name:', err);
    } finally {
      setIsUpdatingName(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!userDetails) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        Usuário não encontrado
      </Alert>
    );
  }

  return (
    <Box 
      sx={{ 
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        width: '100%',
        height: '100%',
        overflow: 'auto',
        pb: 3,
      }}
    >
      {/* Header */}
      <Box 
        display="flex" 
        alignItems="center" 
        gap={2} 
        sx={{ 
          position: 'sticky',
          top: 0,
          bgcolor: 'background.default',
          zIndex: 1,
          py: 2,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/users')}
          variant="outlined"
        >
          Voltar
        </Button>
        <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Detalhes do Usuário
        </Typography>
      </Box>

      {/* Conteúdo */}
      <Paper 
        sx={{ 
          p: { xs: 2, sm: 3 },
          width: '100%',
        }}
      >
        <Grid container spacing={3}>
          {/* Informações básicas */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Informações Básicas
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography><strong>ID:</strong> {userDetails.id}</Typography>
              
              {isEditingName ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1 }}>
                  <TextField
                    size="small"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    disabled={isUpdatingName}
                    autoFocus
                    sx={{ minWidth: 200 }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleUpdateName}
                    disabled={isUpdatingName || !editingName.trim() || editingName === userDetails.name}
                    startIcon={isUpdatingName ? <CircularProgress size={20} /> : null}
                  >
                    {isUpdatingName ? 'Salvando...' : 'Salvar'}
                  </Button>
                  <Button
                    size="small"
                    onClick={() => {
                      setIsEditingName(false);
                      setEditingName(userDetails.name);
                    }}
                    disabled={isUpdatingName}
                  >
                    Cancelar
                  </Button>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography><strong>Nome:</strong> {userDetails.name}</Typography>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setIsEditingName(true);
                      setEditingName(userDetails.name);
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}

              <Typography>
                <strong>Data de Cadastro:</strong> {format(new Date(userDetails.created_date), 'dd/MM/yyyy HH:mm')}
              </Typography>
            </Box>
          </Grid>

          {/* Imagem Biométrica */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Imagem Biométrica
              </Typography>
              {biometryImage && (
                <Button
                  startIcon={<DeleteIcon />}
                  color="error"
                  onClick={() => setShowDeleteDialog(true)}
                  disabled={isDeleting || isUpdating}
                >
                  Deletar Biometria
                </Button>
              )}
            </Box>
            <Box sx={{ mt: 2 }}>
              {loadingImage ? (
                <CircularProgress size={24} />
              ) : (
                <>
                  {biometryImage ? (
                    <Box
                      component="img"
                      src={`data:image/jpeg;base64,${biometryImage}`}
                      alt="Imagem biométrica"
                      sx={{
                        maxWidth: '100%',
                        maxHeight: 300,
                        objectFit: 'contain',
                        borderRadius: 1,
                        mb: 2,
                      }}
                    />
                  ) : (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Usuário não possui imagem biométrica cadastrada
                    </Alert>
                  )}

                  <Box sx={{ mt: 2 }}>
                    <Button
                      component="label"
                      variant="outlined"
                      startIcon={<CloudUploadIcon />}
                      disabled={isUpdating}
                      sx={{ mr: 2 }}
                    >
                      Selecionar Nova Imagem
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleImageChange}
                        disabled={isUpdating}
                      />
                    </Button>

                    {selectedImage && (
                      <Button
                        variant="contained"
                        onClick={handleUpdateBiometry}
                        disabled={isUpdating}
                        startIcon={isUpdating ? <CircularProgress size={20} /> : null}
                      >
                        {isUpdating ? 'Atualizando...' : 'Atualizar Biometria'}
                      </Button>
                    )}

                    {imagePreview && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Preview da nova imagem:
                        </Typography>
                        <img
                          src={imagePreview}
                          alt="Preview"
                          style={{
                            maxWidth: '100%',
                            maxHeight: 300,
                            objectFit: 'contain',
                            borderRadius: 4,
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                </>
              )}
            </Box>
          </Grid>

          {/* Atributos do usuário */}
          <Grid item xs={12}>
            <Divider sx={{ my: 3 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Atributos
              </Typography>
              <Button
                startIcon={<AddIcon />}
                variant="contained"
                onClick={() => setShowCreateDialog(true)}
              >
                Adicionar Atributo
              </Button>
            </Box>
            {userDetails.attributes && userDetails.attributes.length > 0 ? (
              <Box sx={{ mt: 2 }}>
                {userDetails.attributes.map((attr) => (
                  <Box 
                    key={attr.key_name} 
                    sx={{ 
                      mb: 2,
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Box>
                      <Typography>
                        <strong>{attr.key_name}:</strong> {attr.key_value}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Criado em: {format(new Date(attr.created_date), 'dd/MM/yyyy HH:mm')}
                      </Typography>
                    </Box>
                    <IconButton
                      color="error"
                      onClick={() => setDeletingAttribute(attr)}
                      size="small"
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            ) : (
              <Alert severity="info">
                Nenhum atributo cadastrado
              </Alert>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Diálogo de confirmação de deleção */}
      <Dialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          Tem certeza que deseja excluir a biometria deste usuário?
          Esta ação não pode ser desfeita.
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowDeleteDialog(false)}
            disabled={isDeleting}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteBiometry}
            color="error"
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} /> : null}
          >
            {isDeleting ? 'Deletando...' : 'Deletar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de confirmação de deleção de atributo */}
      <Dialog
        open={deletingAttribute !== null}
        onClose={() => setDeletingAttribute(null)}
      >
        <DialogTitle>Confirmar Exclusão de Atributo</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir o atributo:
          </Typography>
          {deletingAttribute && (
            <Box sx={{ mt: 2 }}>
              <Typography>
                <strong>Nome:</strong> {deletingAttribute.key_name}
              </Typography>
              <Typography>
                <strong>Valor:</strong> {deletingAttribute.key_value}
              </Typography>
            </Box>
          )}
          <Typography sx={{ mt: 2, color: 'error.main' }}>
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeletingAttribute(null)}
            disabled={isDeletingAttribute}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteAttribute}
            color="error"
            variant="contained"
            disabled={isDeletingAttribute}
            startIcon={isDeletingAttribute ? <CircularProgress size={20} /> : null}
          >
            {isDeletingAttribute ? 'Deletando...' : 'Deletar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de criação de atributo */}
      <Dialog
        open={showCreateDialog}
        onClose={() => {
          setShowCreateDialog(false);
          setNewAttribute({ key_name: '', key_value: '' });
        }}
      >
        <DialogTitle>Adicionar Novo Atributo</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Nome do Atributo"
              value={newAttribute.key_name}
              onChange={(e) => setNewAttribute(prev => ({ ...prev, key_name: e.target.value }))}
              required
              disabled={isCreatingAttribute}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Valor do Atributo"
              value={newAttribute.key_value}
              onChange={(e) => setNewAttribute(prev => ({ ...prev, key_value: e.target.value }))}
              required
              disabled={isCreatingAttribute}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowCreateDialog(false);
              setNewAttribute({ key_name: '', key_value: '' });
            }}
            disabled={isCreatingAttribute}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleCreateAttribute}
            variant="contained"
            disabled={isCreatingAttribute || !newAttribute.key_name.trim() || !newAttribute.key_value.trim()}
            startIcon={isCreatingAttribute ? <CircularProgress size={20} /> : null}
          >
            {isCreatingAttribute ? 'Criando...' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserDetails; 